import * as React from 'react';
import * as moment from 'moment';
import { get, omit, filter } from 'lodash';
import Dialog from 'material-ui/Dialog';
import * as uuid from 'uuid/v4';
import { eachDay, format } from 'date-fns';
import { withNotifications } from '@components/hoc/withNotifications';
import { breadcrumbs } from '@inqool/uas-web-core/es/utils';
import { getAllDayPlans, editDayPlan, removeDayPlan } from '@modules/hr-dayplans/_dayplansActions';
import { compose, lifecycle, withState, withPropsOnChange, withHandlers } from 'recompose';
import { getShiftCycle } from '@modules/hr-shift-cycles/_shiftCyclesActions';
import { Loader } from '@components/loader';
import { ShiftCycleDetailProps } from './_shiftCyclesTypes';
import DayplanForm from '@modules/hr-shift-cycles/DayplanForm';
import EmployeeCalendar from '@components/employeeCalendar/EmployeeCalendar';
import { Employee } from '@modules/events/_eventsTypes';
import { SelectField, MenuItem } from 'material-ui';

const enhance = compose<any, any>(
  withState('isFetching', 'toggleFetching', true),
  withState('entity', 'fetchEntity', null),
  withState('dayPlans', 'fetchDayPlans', {}),
  withState('employeeId', 'setEmployeeId', null),
  withState('employees', 'fetchEmployees', []),
  withState('dialogOpened', 'toggleDialog', false),
  withState('editedEntities', 'setEditedEntities', null),
  breadcrumbs([{ label: 'Detail směny' }]),
  withNotifications({ getShiftCycle, editDayPlan, removeDayPlan }),
  withHandlers({
    fetchAllShiftCycles: ({
      toggleFetching,
      fetchEntity,
      fetchDayPlans,
      fetchEmployees,
      getShiftCycleNotify,
      match: {
        params: { id }
      }
    }) => async () => {
      toggleFetching(true);
      await getShiftCycleNotify(id).then(
        async response => {
          const employees = get(response, 'employees', []);
          const dayPlansPerEmployee = await Promise.all(
            employees.map(employee =>
              getAllDayPlans([{ field: 'employee.id', operation: 'EQ', value: employee.id }]).then(dayPlans => ({
                [employee.id]: get(dayPlans, 'items')
              }))
            )
          );
          fetchEmployees(employees);
          fetchEntity(response);
          fetchDayPlans(dayPlansPerEmployee);
        },
        error => console.error(error)
      );
      toggleFetching(false);
    }
  }),
  withHandlers({
    changeDayplan: ({ editedEntities, toggleDialog, fetchAllShiftCycles, editDayPlanNotify }) => async ({
      dayPlan
    }) => {
      const employee = get(editedEntities, 'employee');
      const oldDayPlan = get(editedEntities, 'dayPlan');
      const isCreating = get(editedEntities, 'isCreating');

      let newDayPlan = {
        ...dayPlan,
        id: isCreating ? uuid() : get(oldDayPlan, 'id'),
        employee,
        visualizedDate: isCreating
          ? format(get(editedEntities, 'day'), 'YYYY-MM-DD')
          : get(oldDayPlan, 'visualizedDate'),
        periods: dayPlan.periods.map(period => ({
          ...omit(period, 'id'),
          from: moment
            .utc(format(get(editedEntities, 'day', ''), 'YYYY-MM-DD'))
            .hours(get(period, 'from').substring(0, 2))
            .minutes(get(period, 'from').substring(3, 5))
            .format(),
          to: moment
            .utc(format(get(editedEntities, 'day', ''), 'YYYY-MM-DD'))
            .hours(get(period, 'to').substring(0, 2))
            .minutes(get(period, 'to').substring(3, 5))
            .add(get(period, 'from') > get(period, 'to') ? 1 : 0, 'd')
            .format()
        }))
      };

      await editDayPlanNotify(newDayPlan).then(
        async () => {
          await fetchAllShiftCycles();
          toggleDialog(false);
        },
        error => console.debug(error)
      );
    },
    removeSingleDayPlan: ({ fetchAllShiftCycles, toggleDialog, removeDayPlanNotify }) => async (id: string) => {
      await removeDayPlanNotify(id).then(
        async () => {
          await fetchAllShiftCycles();
          toggleDialog(false);
        },
        error => console.debug(error)
      );
    }
  }),
  lifecycle<any, any>({
    async componentDidMount() {
      await this.props.fetchAllShiftCycles();
    }
  }),
  withPropsOnChange(['entity'], ({ entity }) => ({
    days: entity ? eachDay(get(entity, 'from'), get(entity, 'to')) : []
  }))
);

const View: React.SFC<ShiftCycleDetailProps> = ({
  entity,
  dayPlans,
  days,
  isFetching,
  dialogOpened,
  toggleDialog,
  setEditedEntities,
  editedEntities,
  changeDayplan,
  removeSingleDayPlan,
  employees,
  employeeId,
  setEmployeeId
}) => (
  <>
    {isFetching && <Loader />}
    <h1>Detail směny: {get(entity, 'label', '')}</h1>
    {entity && (
      <h3>
        {format(get(entity, 'from'), 'DD. MM. YYYY')} - {format(get(entity, 'to'), 'DD. MM. YYYY')}
      </h3>
    )}
    {!isFetching && (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginRight: 15 }}>Filtrovat zaměstnance:</div>
        <SelectField value={employeeId} onChange={(_e, _i, value) => setEmployeeId(value)}>
          {[{ id: null, fullName: 'Všichni zaměstnanci' }, ...employees].map((e: Employee) => (
            <MenuItem key={e.id} value={e.id} primaryText={e.fullName} />
          ))}
        </SelectField>
      </div>
    )}
    <EmployeeCalendar
      days={days}
      employees={
        employeeId
          ? filter(get(entity, 'employees', []), employee => employee.id === employeeId)
          : get(entity, 'employees', [])
      }
      dayPlans={dayPlans}
      toggleDialog={toggleDialog}
      setEditedEntities={setEditedEntities}
      entity={entity}
      canChangeDayPlans={true}
      showEndOfWeekCalendars={true}
      linkToEmployeeActive={true}
    />
    <Dialog
      open={dialogOpened}
      title={` ${get(editedEntities, 'isCreating') ? 'Nový denní plán' : 'Změna denního plánu'} pro osobu ${get(
        editedEntities,
        'employee.fullName',
        ''
      )}  dne ${format(get(editedEntities, 'day', ''), 'DD. MM. YYYY')}`}
      onRequestClose={() => {
        toggleDialog(false);
      }}
    >
      <DayplanForm
        {...editedEntities}
        toggleDialog={toggleDialog}
        onSubmit={changeDayplan}
        removeDayPlan={removeSingleDayPlan}
      />
    </Dialog>
  </>
);

export const ShiftCycleDetail = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/hr-shift-cycles/ShiftCycleDetail.tsx